import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { startFragmentExchange } from "./fragment-secret";
import styles from "./AuthScreen.module.css";

export type SimulationScreenProps = {
  readonly onComplete: () => void;
  readonly onRejectIdentity: () => void;
};

type SimulationView = "loading" | "ready" | "conflict" | "error";

export function SimulationScreen({ onComplete, onRejectIdentity }: SimulationScreenProps) {
  const { t } = useTranslation();
  const [view, setView] = useState<SimulationView>("loading");
  const callbackSentRef = useRef(false);

  useEffect(() => {
    let active = true;
    void startFragmentExchange().then((result) => {
      if (!active) return;
      if (result.kind === "simulate" && result.status === "complete") {
        setView("ready");
        if (!callbackSentRef.current) {
          callbackSentRef.current = true;
          onComplete();
        }
        return;
      }
      if (
        result.kind === "simulate" &&
        result.status === "error" &&
        result.error.code === "IDENTITY_CONFLICT"
      ) {
        setView("conflict");
        if (!callbackSentRef.current) {
          callbackSentRef.current = true;
          onRejectIdentity();
        }
        return;
      }
      setView("error");
    });
    return () => {
      active = false;
    };
  }, [onComplete, onRejectIdentity]);

  return (
    <section className={styles.screen} aria-labelledby="simulation-title" aria-busy={view === "loading"}>
      <div className={styles.card}>
        <header className={styles.header}>
          <p className={styles.eyebrow}>{t("app.name")}</p>
          <h1 id="simulation-title">{t("auth.simulation.title")}</h1>
          <p>{t("auth.simulation.description")}</p>
        </header>

        {view === "loading" ? <p className={styles.loadingText}>{t("auth.simulation.loading")}</p> : null}
        {view === "ready" ? <p className={styles.help}>{t("auth.simulation.ready")}</p> : null}
        {view === "conflict" ? (
          <section className={styles.choice} aria-labelledby="simulation-conflict-title">
            <h2 id="simulation-conflict-title">{t("auth.simulation.conflictTitle")}</h2>
            <p className={styles.help}>{t("auth.simulation.conflictHelp")}</p>
            <button className={styles.primaryButton} type="button" onClick={onRejectIdentity}>
              {t("auth.simulation.return")}
            </button>
          </section>
        ) : null}

        <div className={styles.messageRegion} data-error={view === "error"} aria-live="polite" aria-atomic="true">
          <p className={styles.errorMessage}>
            {view === "error" ? t("auth.errors.invalidLink") : "\u00a0"}
          </p>
        </div>
      </div>
    </section>
  );
}
